import { TaskStatus } from '@lit/task';
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { Ref } from 'lit/directives/ref.js';

import { setDocumentTitle } from './document_title.js';
import type { ITabLeaf } from './tab_leaf.js';

/** What a tab page declares of its leaves. */
interface ITabLeafOrchestrationOptions<Tab extends string> {

    /** One ref per tab, each bound to its leaf in the page's render. */
    refs: Record<Tab, Ref<ITabLeaf>>;

    /** The tab the address names. */
    requestedTab: () => Tab;

    /** The tab the page renders, the fallback where the address names one the caller may not see. */
    effectiveTab: () => Tab;

    /** The tab the page falls back to. */
    fallbackTab: Tab;

    /** The document title of the fallback tab, for an address naming a tab the caller may not see. */
    fallbackTitle: string;

    /** Where the page's access snapshot stands; the leaves are refreshed only once it is known. */
    accessStatus: () => TaskStatus;
}

/**
 * Refreshes a tab page's leaves: the active one first and the others behind it once the access
 * snapshot is known, then the newly active one whenever the effective tab changes.
 *
 * The refresh runs after the update rather than in `willUpdate`: Lit calls `willUpdate` before
 * the reactive controllers' `hostUpdate`, so the access `Task` is still `INITIAL` when an identity
 * change is observed, and its `taskComplete` resolves with `undefined` for that status. After the
 * update, the render that materialized the visibility-gated leaves has run and every ref is bound.
 * The same hook covers both a route change and a tab whose visibility resolves only later.
 */
export class TabLeafOrchestration<Tab extends string> implements ReactiveController {
    private lastActiveTab: Tab | null = null;

    private bootstrapPending = false;

    /**
     * @param host - The tab page; register the controller after its access `Task`.
     * @param options - The page's leaves and tabs.
     */
    constructor (host: ReactiveControllerHost, private readonly options: ITabLeafOrchestrationOptions<Tab>) {
        host.addController(this);
    }

    /**
     * The leaves' refs, for the page's render to bind.
     * @returns One ref per tab.
     */
    get refs (): Record<Tab, Ref<ITabLeaf>> {
        return this.options.refs;
    }

    /** Starts over for a new subject, from the page's `willUpdate`. */
    restart (): void {
        this.lastActiveTab = null;
        this.bootstrapPending = true;
    }

    hostUpdated (): void {
        if (this.bootstrapPending) {
            this.bootstrapOnceAccessSettled();

            return;
        }

        const effectiveTab = this.options.effectiveTab();

        if (effectiveTab !== this.lastActiveTab) {
            this.lastActiveTab = effectiveTab;
            void this.refs[effectiveTab].value?.refresh();
            this.nameFallbackTab();
        }
    }

    /** Titles the document after the fallback tab when the address names a tab this caller may not see. */
    private nameFallbackTab (): void {
        const effectiveTab = this.options.effectiveTab();

        if (effectiveTab !== this.options.requestedTab() && effectiveTab === this.options.fallbackTab) {
            setDocumentTitle(this.options.fallbackTitle);
        }
    }

    private bootstrapOnceAccessSettled (): void {
        const status = this.options.accessStatus();

        if (status !== TaskStatus.COMPLETE && status !== TaskStatus.ERROR) {
            return;
        }

        this.bootstrapPending = false;
        this.lastActiveTab = this.options.effectiveTab();
        this.nameFallbackTab();

        if (status === TaskStatus.ERROR) {
            return;
        }

        void this.refreshAll();
    }

    private async refreshAll (): Promise<void> {
        const activeTab = this.options.effectiveTab();
        await this.refs[activeTab].value?.refresh();

        setTimeout(() => {
            for (const [
                name, leaf,
            ] of Object.entries<Ref<ITabLeaf>>(this.refs)) {
                if (name !== activeTab) {
                    void leaf.value?.refresh().catch(() => undefined);
                }
            }
        }, 0);
    }
}

/**
 * The access snapshot a tab page gates its strip, its panels and its leaves on: the denial while
 * the access `Task` has produced no value yet and after it errored, and the previous value during a
 * re-run, so a permissions bump does not collapse the tab strip mid-flight.
 * @param task - The page's access `Task`.
 * @param denied - The snapshot that grants nothing.
 * @returns The snapshot to gate on.
 */
export function settledAccess<TAccess> (task: { status: TaskStatus;
    value?: TAccess; }, denied: TAccess): TAccess {
    if (task.status === TaskStatus.ERROR) {
        return denied;
    }

    return task.value ?? denied;
}
